import React from "react";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as actions from "../../../../store/actions/actions";
import { withNamespaces } from "react-i18next";
import CONTEXTUAL_HELP from "../data/contextualHelp";
import { getVisibleRegistry } from "../utils/visibleRegistry";
import { recencyOf } from "../utils/docDates";
import RecencyBadge from "./RecencyBadge";
import HELP_CATEGORIES from "../data/categories";

const styles = {
  backdrop: {
    position: "fixed",
    top: 55,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0, 0, 0, 0.3)",
    zIndex: 1040,
  },
  drawer: {
    position: "fixed",
    top: 55,
    right: 0,
    width: 420,
    maxWidth: "100vw",
    height: "calc(100vh - 55px)",
    background: "white",
    boxShadow: "-4px 0 24px rgba(0, 0, 0, 0.1)",
    zIndex: 1050,
    display: "flex",
    flexDirection: "column",
    overflowY: "auto",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 20,
    borderBottom: "1px solid #e5e7eb",
  },
  closeBtn: {
    border: "none",
    background: "none",
    cursor: "pointer",
    color: "#9ca3af",
    padding: 4,
    lineHeight: 1,
  },
  section: {
    padding: "16px 20px",
    borderBottom: "1px solid #f3f4f6",
  },
  sectionTitle: {
    fontSize: "0.75rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: "#9ca3af",
    marginBottom: 12,
    display: "flex",
    alignItems: "center",
  },
  articleBtn: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    padding: "10px 12px",
    border: "none",
    background: "none",
    cursor: "pointer",
    borderRadius: 8,
    fontSize: "0.875rem",
    color: "#374151",
    transition: "background-color 0.15s",
  },
  fullLink: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    padding: 12,
    border: "1px solid #667eea",
    background: "none",
    color: "#667eea",
    fontWeight: 600,
    fontSize: "0.875rem",
    borderRadius: 8,
    cursor: "pointer",
  },
};

class DocDrawer extends React.Component {
  handleClose = () => {
    this.props.actions.changeSetting("helpDrawerOpen", false);
  };

  handleOpenArticle = (slug) => {
    this.handleClose();
    this.props.history.push(`/HelpCenterView?article=${slug}`);
  };

  handleOpenFullDocs = () => {
    this.handleClose();
    this.props.history.push("/HelpCenterView");
  };

  render() {
    const { t, settings, location } = this.props;
    const isOpen = settings.helpDrawerOpen;

    if (!isOpen) return null;

    const registry = getVisibleRegistry();

    // Get contextual articles based on current route
    const currentPath = location.pathname;
    // Certaines vues sont deep-linkées (ex. /VeterinaryDashboardView/clinical) :
    // retomber sur le premier segment pour retrouver l'aide contextuelle.
    const basePath = '/' + (currentPath.split('/')[1] || '');
    const contextualSlugs = CONTEXTUAL_HELP[currentPath] || CONTEXTUAL_HELP[basePath] || [];
    const contextualArticles = contextualSlugs
      .map((slug) => registry.find((a) => a.slug === slug))
      .filter(Boolean);

    // Get all categories for browse
    const categoriesWithArticles = HELP_CATEGORIES.map((cat) => ({
      ...cat,
      articles: registry.filter((a) => a.categoryId === cat.id),
    })).filter((cat) => cat.articles.length > 0);

    return (
      <>
        {/* Backdrop */}
        <div style={styles.backdrop} onClick={this.handleClose} />

        {/* Drawer */}
        <div style={styles.drawer}>
          {/* Header */}
          <div style={styles.header}>
            <h5 style={{ margin: 0, fontWeight: 600, color: "#1f2937" }}>
              <i className="fas fa-book" style={{ marginRight: 10, color: "#667eea" }}></i>
              {t("helpCenter.title")}
            </h5>
            <button style={styles.closeBtn} onClick={this.handleClose}>
              <span aria-hidden="true" style={{ fontSize: "1.5rem" }}>&times;</span>
            </button>
          </div>

          {/* Contextual help */}
          {contextualArticles.length > 0 && (
            <div style={styles.section}>
              <div style={styles.sectionTitle}>
                <i className="fas fa-magic" style={{ marginRight: 8, color: "#667eea" }}></i>
                {t("helpCenter.drawer.contextual")}
              </div>
              {contextualArticles.map((article) => {
                const cat = HELP_CATEGORIES.find((c) => c.id === article.categoryId);
                return (
                  <button
                    key={article.slug}
                    style={styles.articleBtn}
                    onClick={() => this.handleOpenArticle(article.slug)}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f3f4f6")}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                  >
                    <i
                      className={cat?.icon || "fas fa-file"}
                      style={{ color: cat?.color || "#667eea", marginRight: 10, width: 16, textAlign: "center" }}
                    ></i>
                    <span>{t(`helpCenter.articles.${article.slug}.title`)}</span>
                    <span style={{ marginLeft: 8 }}>
                      <RecencyBadge recency={recencyOf(article)} t={t} />
                    </span>
                    <i className="fas fa-arrow-right" style={{ marginLeft: "auto", fontSize: "0.7rem", opacity: 0.3 }}></i>
                  </button>
                );
              })}
            </div>
          )}

          {/* Browse all */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>
              <i className="fas fa-compass" style={{ marginRight: 8, color: "#667eea" }}></i>
              {t("helpCenter.drawer.browse")}
            </div>
            {categoriesWithArticles.map((cat) => (
              <button
                key={cat.id}
                style={styles.articleBtn}
                onClick={() => this.handleOpenArticle(cat.articles[0].slug)}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f3f4f6")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
              >
                <i
                  className={cat.icon}
                  style={{ color: cat.color, marginRight: 10, width: 16, textAlign: "center" }}
                ></i>
                <span>{t(`helpCenter.categories.${cat.id}`)}</span>
                <span
                  style={{
                    marginLeft: "auto",
                    fontSize: "0.75rem",
                    color: "#9ca3af",
                    backgroundColor: "#f3f4f6",
                    padding: "2px 8px",
                    borderRadius: 10,
                  }}
                >
                  {cat.articles.length}
                </span>
              </button>
            ))}
          </div>

          {/* Full docs link */}
          <div style={{ padding: "16px 20px" }}>
            <button
              style={styles.fullLink}
              onClick={this.handleOpenFullDocs}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#667eea";
                e.currentTarget.style.color = "white";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
                e.currentTarget.style.color = "#667eea";
              }}
            >
              <i className="fas fa-external-link-alt" style={{ marginRight: 8 }}></i>
              {t("helpCenter.drawer.viewAll")}
            </button>
          </div>
        </div>
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  settings: state.settings,
});
const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(actions, dispatch),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(withNamespaces("translations")(DocDrawer)));
